import { Target } from '@agilelab/plugin-wb-builder-backend';
import {
  Action,
  generateURNByKind,
  ProvisioningPreviewResponse,
} from '@agilelab/plugin-wb-builder-common';
import { useApi } from '@mainstage/core-plugin-api';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useState,
} from 'react';
import yaml from 'yaml';
import { panelCatalogApiRef } from '../../../../api';
import { useReleaseDetailPageContext } from '../../context/useReleaseDetailPageContext';
import { DeploymentPreview } from '../../types';
import { getAllowedActions } from '../utils';

export type DeploymentPreviewContextProps = {
  isAdvanced: boolean;
  setIsAdvanced: Dispatch<SetStateAction<boolean>>;

  previews: DeploymentPreview[];
  setPreviews: Dispatch<SetStateAction<DeploymentPreview[]>>;

  cleanUpPreviews: DeploymentPreview[];
  setCleanUpPreviews: Dispatch<SetStateAction<DeploymentPreview[]>>;

  temporarySelectedItems: DeploymentPreview[];
  setTemporarySelectedItems: Dispatch<SetStateAction<DeploymentPreview[]>>;

  onChangeActions: (action: Action) => void;

  checkPreview: (
    targetStatus: Target[],
  ) => Promise<ProvisioningPreviewResponse>;
  partialProvision: (
    targetStatus: Target[],
  ) => Promise<ProvisioningPreviewResponse>;
};

export const DeploymentPreviewContext =
  createContext<DeploymentPreviewContextProps>(
    {} as DeploymentPreviewContextProps,
  );

type Props = {
  children?: ReactNode;
};

export const DeploymentPreviewContextProvider: FC<Props> = ({ children }) => {
  const { environment, queryParamEnvironment, release, currentDescriptor } =
    useReleaseDetailPageContext();
  const [isAdvanced, setIsAdvanced] = useState<boolean>(false);
  const [previews, setPreviews] = useState<DeploymentPreview[]>([]);
  const [cleanUpPreviews, setCleanUpPreviews] = useState<DeploymentPreview[]>(
    [],
  );
  const [temporarySelectedItems, setTemporarySelectedItems] = useState<
    DeploymentPreview[]
  >([]);

  const panelCatalogApi = useApi(panelCatalogApiRef);

  const deploymentUnitId = generateURNByKind(
    release.metadata.projectName ?? release.metadata.dataProductName,
    release.metadata.projectKind ?? 'system',
  );
  const selectedEnvironment = queryParamEnvironment || environment.name;

  const checkPreview = useCallback(
    async (targetStatus: Target[]) =>
      panelCatalogApi.getDeploymentUnitsProvisioningPreview(
        deploymentUnitId,
        selectedEnvironment,
        {
          descriptor: yaml.parse(currentDescriptor),
          targetStatus,
          removeData: false,
          preventRedeploy: false,
        },
      ),
    [currentDescriptor, deploymentUnitId, panelCatalogApi, selectedEnvironment],
  );

  const partialProvision = useCallback(
    async (targetStatus: Target[]) =>
      panelCatalogApi.partialProvision(
        deploymentUnitId,
        selectedEnvironment,
        {
          descriptor: yaml.parse(currentDescriptor),
          targetStatus,
          removeData: false,
          preventRedeploy: false,
        },
        false,
      ),
    [currentDescriptor, deploymentUnitId, panelCatalogApi, selectedEnvironment],
  );

  const onChangeActions = (action: Action) => {
    setPreviews(selected => {
      const newPreviews = selected.map(preview => {
        const isTopLevelSelected = temporarySelectedItems.some(
          item => item.id === preview.id,
        );

        let updatedPreview = preview;

        if (isTopLevelSelected) {
          const allowedActions = getAllowedActions(
            preview.status,
            preview.newState!,
          );
          if (allowedActions?.includes(action)) {
            updatedPreview = { ...updatedPreview, action };
          }
        }

        if (preview.components?.length) {
          const updatedComponents = preview.components.map(subComponent => {
            if (
              temporarySelectedItems.some(item => item.id === subComponent.id)
            ) {
              const allowedActions = getAllowedActions(
                subComponent.status,
                subComponent.newState!,
              );
              if (allowedActions?.includes(action)) {
                return { ...subComponent, action };
              }
            }
            return subComponent;
          });
          updatedPreview = { ...updatedPreview, components: updatedComponents };
        }

        return updatedPreview;
      });
      return newPreviews;
    });
    setTemporarySelectedItems([]);
  };

  return (
    <DeploymentPreviewContext.Provider
      value={{
        isAdvanced,
        setIsAdvanced,

        previews,
        setPreviews,

        cleanUpPreviews,
        setCleanUpPreviews,

        onChangeActions,

        checkPreview,
        partialProvision,

        temporarySelectedItems,
        setTemporarySelectedItems,
      }}
    >
      {children}
    </DeploymentPreviewContext.Provider>
  );
};
